import React, { useState } from 'react';
import {
  Drawer,
  Button,
  Space,
  Typography,
  Card,
  Row,
  Col,
  Avatar,
  Tag,
  message,
  Empty,
  Image,
} from 'antd';
import {
  SkinOutlined,
  RobotOutlined,
  SyncOutlined,
  EyeOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { SourceScene, SourceCharacter, SceneOutfit, SourceCharacterCheckpoint } from '../../../types';
import { sourceStoriesApi } from '../../../services/api';

const { Text, Paragraph } = Typography;

interface SceneOutfitsDrawerProps {
  open: boolean;
  scene: SourceScene;
  storyId: string;
  characters: SourceCharacter[];
  checkpoints: SourceCharacterCheckpoint[];
  previousScenes: SourceScene[];
  onClose: () => void;
  onOutfitGenerated: (sceneId: string, updatedOutfits: SceneOutfit[]) => void;
  isMobile: boolean;
}

const SceneOutfitsDrawer: React.FC<SceneOutfitsDrawerProps> = ({
  open,
  scene,
  storyId,
  characters,
  checkpoints,
  previousScenes,
  onClose,
  onOutfitGenerated,
  isMobile,
}) => {
  const [generatingIds, setGeneratingIds] = useState<string[]>([]);
  const [selectingIds, setSelectingIds] = useState<string[]>([]);
  const [expandedGalleryId, setExpandedGalleryId] = useState<string | null>(null);

  const handleGenerate = async (characterId: string) => {
    setGeneratingIds((prev) => [...prev, characterId]);
    try {
      const result = await sourceStoriesApi.generateSceneOutfit(storyId, scene.id, characterId);
      const newOutfit = result.data;
      
      const currentOutfits = [...(scene.outfits || [])];
      const existingIdx = currentOutfits.findIndex(o => o.characterId === characterId);
      if (existingIdx >= 0) {
        currentOutfits[existingIdx] = newOutfit;
      } else {
        currentOutfits.push(newOutfit);
      }
      
      onOutfitGenerated(scene.id, currentOutfits);
      message.success(`Đã tạo outfit cho ${characters.find(c => c.id === characterId)?.name}`);
    } catch (error: any) {
      message.error(`Lỗi khi tạo outfit: ${error.message}`);
    } finally {
      setGeneratingIds((prev) => prev.filter((id) => id !== characterId));
    }
  };

  const handleSelect = async (characterId: string, imageUrl: string) => {
    setSelectingIds((prev) => [...prev, characterId]);
    try {
      const result = await sourceStoriesApi.selectSceneOutfit(storyId, scene.id, characterId, imageUrl);
      const newOutfit = result.data;

      const currentOutfits = [...(scene.outfits || [])];
      const existingIdx = currentOutfits.findIndex(o => o.characterId === characterId);
      if (existingIdx >= 0) {
        currentOutfits[existingIdx] = newOutfit;
      } else {
        currentOutfits.push(newOutfit);
      }

      onOutfitGenerated(scene.id, currentOutfits);
      setExpandedGalleryId(null);
      message.success(`Đã chọn outfit cho ${characters.find(c => c.id === characterId)?.name}`);
    } catch (error: any) {
      message.error(`Lỗi khi chọn outfit: ${error.message}`);
    } finally {
      setSelectingIds((prev) => prev.filter((id) => id !== characterId));
    }
  };

  const sceneCharacters = characters.filter((c) => (scene.characterIds || []).includes(c.id));
  const outfitCount = (scene.outfits || []).length;

  return (
    <Drawer
      title={
        <Space>
          <SkinOutlined />
          <span>
            {scene.title} — {outfitCount}/{sceneCharacters.length} outfits
          </span>
        </Space>
      }
      placement={isMobile ? 'bottom' : 'right'}
      width={isMobile ? '100%' : 480}
      height={isMobile ? '85vh' : undefined}
      onClose={onClose}
      open={open}
    >
      {sceneCharacters.length === 0 ? (
        <Empty description="Scene này không có nhân vật nào." />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {sceneCharacters.map((char) => {
            const outfit = (scene.outfits || []).find((o) => o.characterId === char.id);
            const isGenerating = generatingIds.includes(char.id);

            return (
              <Card key={char.id} size="small" style={{ borderRadius: 8 }}>
                <Row gutter={16} align="middle">
                  {/* Left: Identity */}
                  <Col span={4} style={{ textAlign: 'center' }}>
                    <Avatar
                      src={char.avatarUrl}
                      size={40}
                      icon={<RobotOutlined />}
                      style={{ marginBottom: 4 }}
                    />
                    <div style={{ fontSize: 10, fontWeight: 'bold', wordBreak: 'break-word', lineHeight: '1.2' }}>{char.name}</div>
                  </Col>

                  {/* Middle: Outfit Image */}
                  <Col span={5}>
                    <div
                      style={{
                        width: '100%',
                        maxWidth: 80,
                        margin: '0 auto',
                        aspectRatio: '9/16',
                        background: '#f5f5f5',
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        border: '1px solid #d9d9d9',
                      }}
                    >
                      {outfit ? (
                        <Image
                          src={outfit.imageUrl}
                          alt="Outfit"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }}
                          preview={{ mask: <EyeOutlined /> }}
                        />
                      ) : (
                        <div style={{ textAlign: 'center', color: '#bfbfbf' }}>
                          <SkinOutlined style={{ fontSize: 20 }} />
                          <div style={{ fontSize: 9 }}>Chưa có outfit</div>
                        </div>
                      )}
                    </div>
                    {outfit && (
                      <div style={{ textAlign: 'center', marginTop: 4 }}>
                        {outfit.note === 'Chọn thủ công' ? (
                          <Tag color="purple" style={{ fontSize: 9, margin: 0, padding: '0 4px' }}>
                            Thủ công
                          </Tag>
                        ) : outfit.canInheritFromPrevScene ? (
                          <Tag color="success" style={{ fontSize: 9, margin: 0, padding: '0 4px' }}>
                            Kế thừa
                          </Tag>
                        ) : (
                          <Tag color="orange" style={{ fontSize: 9, margin: 0, padding: '0 4px' }}>
                            Mới
                          </Tag>
                        )}
                      </div>
                    )}
                  </Col>

                  {/* Right: Info & Actions */}
                  <Col span={15}>
                    {outfit ? (
                      <div style={{ marginBottom: 8 }}>
                        <Paragraph
                          ellipsis={{ rows: 2, tooltip: outfit.outfitDescription }}
                          style={{ fontSize: 11, marginBottom: 4 }}
                        >
                          {outfit.outfitDescription || (outfit.note === 'Chọn thủ công' ? 'Đã chọn ảnh thủ công.' : '')}
                        </Paragraph>
                        <Text type="secondary" italic style={{ fontSize: 10, display: 'block' }}>
                          {outfit.note}
                        </Text>
                      </div>
                    ) : (
                      <div style={{ marginBottom: 8, minHeight: 40 }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Nhấn Generate để AI thiết kế trang phục cho scene này.
                        </Text>
                      </div>
                    )}

                    <Space direction="vertical" style={{ width: '100%' }} size={4}>
                      <Button
                        type={outfit ? 'default' : 'primary'}
                        size="small"
                        block
                        icon={isGenerating ? <SyncOutlined spin /> : <RobotOutlined />}
                        onClick={() => handleGenerate(char.id)}
                        loading={isGenerating}
                        disabled={selectingIds.includes(char.id)}
                        style={{ fontSize: 11 }}
                      >
                        {outfit ? 'Regenerate' : 'Generate'}
                      </Button>
                      <Button
                        size="small"
                        block
                        type="dashed"
                        icon={<PictureOutlined />}
                        onClick={() => setExpandedGalleryId(expandedGalleryId === char.id ? null : char.id)}
                        disabled={isGenerating || selectingIds.includes(char.id)}
                        style={{ fontSize: 11 }}
                      >
                        {expandedGalleryId === char.id ? "Đóng gallery" : "Chọn ảnh có sẵn"}
                      </Button>
                    </Space>
                  </Col>
                </Row>

                {/* Inline Gallery */}
                {expandedGalleryId === char.id && (
                  <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                    <div 
                      style={{ 
                        display: 'flex', 
                        overflowX: 'auto', 
                        gap: 8, 
                        paddingBottom: 8,
                        scrollbarWidth: 'thin'
                      }}
                    >
                      {/* Source 1: Checkpoints */}
                      {checkpoints.filter(cp => cp.characterId === char.id && cp.avatarUrl).map(cp => (
                        <div 
                          key={cp.id}
                          style={{ 
                            flex: '0 0 56px', 
                            cursor: selectingIds.includes(char.id) ? 'not-allowed' : 'pointer',
                            opacity: selectingIds.includes(char.id) ? 0.5 : 1
                          }}
                          onClick={() => !selectingIds.includes(char.id) && handleSelect(char.id, cp.avatarUrl!)}
                        >
                          <div style={{ width: 56, height: 80, borderRadius: 4, overflow: 'hidden', border: '1px solid #d9d9d9' }}>
                            <img 
                              src={cp.avatarUrl!} 
                              alt={cp.name} 
                              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} 
                            />
                          </div>
                          <div style={{ fontSize: 9, textAlign: 'center', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {cp.name}
                          </div>
                        </div>
                      ))}

                      {/* Source 2: Previous Outfits */}
                      {previousScenes
                        .filter(s => (s.outfits as any as SceneOutfit[] || []).some(o => o.characterId === char.id && o.imageUrl))
                        .map(s => {
                          const prevOutfit = (s.outfits as any as SceneOutfit[]).find(o => o.characterId === char.id)!;
                          return (
                            <div 
                              key={s.id}
                              style={{ 
                                flex: '0 0 56px', 
                                cursor: selectingIds.includes(char.id) ? 'not-allowed' : 'pointer',
                                opacity: selectingIds.includes(char.id) ? 0.5 : 1
                              }}
                              onClick={() => !selectingIds.includes(char.id) && handleSelect(char.id, prevOutfit.imageUrl)}
                            >
                              <div style={{ width: 56, height: 80, borderRadius: 4, overflow: 'hidden', border: '1px solid #d9d9d9' }}>
                                <img 
                                  src={prevOutfit.imageUrl} 
                                  alt={s.title} 
                                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} 
                                />
                              </div>
                              <div style={{ fontSize: 9, textAlign: 'center', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {s.title}
                              </div>
                            </div>
                          );
                        })}
                      
                      {checkpoints.filter(cp => cp.characterId === char.id && cp.avatarUrl).length === 0 && 
                       previousScenes.filter(s => (s.outfits as any as SceneOutfit[] || []).some(o => o.characterId === char.id && o.imageUrl)).length === 0 && (
                        <div style={{ width: '100%', padding: '8px 0' }}>
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có ảnh nào" style={{ margin: 0 }} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </Space>
      )}
    </Drawer>
  );
};

export default SceneOutfitsDrawer;
